/**
 * <dark-reviews-carousel>
 *
 * Slide-tabanlı yorum carousel'i. Desktop'ta transform-X ile kayar,
 * mobilde native scroll-snap kullanır (JS dokunmaz).
 *
 * Slide grupları = ceil(totalSlides / cardsPerView).
 * Dot sayısı slide grubu kadar; prev/next bir grup ileri/geri atlar.
 */
class DarkReviewsCarousel extends HTMLElement {
  #track = null;
  #viewport = null;
  #dotsContainer = null;
  #prevBtn = null;
  #nextBtn = null;
  #slides = [];
  #dots = [];
  #groupCount = 1;
  #currentGroup = 0;
  #autoplayTimer = null;
  #autoplay = false;
  #interval = 6000;
  #isMobile = false;
  #mql = null;
  #abortController = new AbortController();

  connectedCallback() {
    this.#track = this.querySelector('[data-reviews-track]');
    this.#viewport = this.querySelector('[data-reviews-viewport]');
    this.#dotsContainer = this.querySelector('[data-reviews-dots]');
    this.#prevBtn = this.querySelector('[data-reviews-prev]');
    this.#nextBtn = this.querySelector('[data-reviews-next]');
    this.#slides = Array.from(this.querySelectorAll('[data-reviews-slide]'));

    if (!this.#track || this.#slides.length === 0) return;

    this.#autoplay = this.dataset.autoplay === 'true';
    this.#interval = parseInt(this.dataset.interval || '6000', 10) || 6000;

    this.#mql = window.matchMedia('(max-width: 749px)');
    this.#isMobile = this.#mql.matches;

    const { signal } = this.#abortController;
    this.#mql.addEventListener('change', this.#handleMediaChange, { signal });

    this.#prevBtn?.addEventListener('click', () => this.#step(-1), { signal });
    this.#nextBtn?.addEventListener('click', () => this.#step(1), { signal });

    this.addEventListener('mouseenter', this.#pauseAutoplay, { signal });
    this.addEventListener('mouseleave', this.#startAutoplay, { signal });
    this.addEventListener('focusin', this.#pauseAutoplay, { signal });
    this.addEventListener('focusout', this.#startAutoplay, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibility, { signal });

    this.#computeGroups();
    this.#renderDots();
    this.#applyPosition();
    this.#updateButtons();
    this.#startAutoplay();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#pauseAutoplay();
  }

  #cardsPerView() {
    if (this.#isMobile) return 1;
    const styleVal = parseInt(getComputedStyle(this).getPropertyValue('--reviews-per-view'), 10);
    if (Number.isFinite(styleVal) && styleVal > 0) {
      if (window.matchMedia('(max-width: 1024px)').matches) return Math.min(2, styleVal);
      return styleVal;
    }
    return 3;
  }

  #computeGroups() {
    const perView = this.#cardsPerView();
    this.#groupCount = Math.max(1, Math.ceil(this.#slides.length / perView));
    if (this.#currentGroup >= this.#groupCount) this.#currentGroup = this.#groupCount - 1;
  }

  #renderDots() {
    if (!this.#dotsContainer) return;
    this.#dotsContainer.innerHTML = '';
    this.#dots = [];
    if (this.#groupCount <= 1) {
      this.#dotsContainer.hidden = true;
      return;
    }
    this.#dotsContainer.hidden = false;
    for (let i = 0; i < this.#groupCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'dark-reviews__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `${i + 1}. yorum grubuna git`);
      if (i === this.#currentGroup) dot.classList.add('is-active');
      dot.addEventListener('click', () => this.#goTo(i), { signal: this.#abortController.signal });
      this.#dotsContainer.appendChild(dot);
      this.#dots.push(dot);
    }
  }

  #applyPosition() {
    if (this.#isMobile) {
      this.#track.style.transform = '';
      return;
    }
    const offset = this.#currentGroup * 100;
    this.#track.style.transform = `translateX(-${offset}%)`;
  }

  #updateButtons() {
    if (!this.#prevBtn || !this.#nextBtn) return;
    this.#prevBtn.disabled = this.#currentGroup === 0;
    this.#nextBtn.disabled = this.#currentGroup >= this.#groupCount - 1;
  }

  #step(direction) {
    const next = this.#currentGroup + direction;
    if (next < 0 || next >= this.#groupCount) return;
    this.#goTo(next);
  }

  #goTo(index) {
    this.#currentGroup = Math.max(0, Math.min(index, this.#groupCount - 1));
    this.#applyPosition();
    this.#updateButtons();
    this.#dots.forEach((d, i) => d.classList.toggle('is-active', i === this.#currentGroup));
  }

  #startAutoplay = () => {
    if (!this.#autoplay || this.#groupCount <= 1 || this.#isMobile) return;
    this.#pauseAutoplay();
    this.#autoplayTimer = setInterval(() => {
      const next = (this.#currentGroup + 1) % this.#groupCount;
      this.#goTo(next);
    }, this.#interval);
  };

  #pauseAutoplay = () => {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  };

  #handleVisibility = () => {
    if (document.hidden) this.#pauseAutoplay();
    else this.#startAutoplay();
  };

  #handleMediaChange = (event) => {
    this.#isMobile = event.matches;
    this.#computeGroups();
    this.#renderDots();
    this.#applyPosition();
    this.#updateButtons();
    if (this.#isMobile) this.#pauseAutoplay();
    else this.#startAutoplay();
  };
}

if (!customElements.get('dark-reviews-carousel')) {
  customElements.define('dark-reviews-carousel', DarkReviewsCarousel);
}
